/**
 * ProductReviewForm Component
 * 
 * İkinci resimdeki tasarıma uygun review form komponenti
 * Rating kategorileri, form alanları ve sözleşme checkbox'ları
 */

"use client";

import React, { useState, useEffect, useCallback, useRef, useId, useMemo } from "react";
import dynamic from "next/dynamic";
import { motion } from "framer-motion";
import { formItemVariants, buttonVariants } from "@/lib/animations";
import {
    loadRecaptchaScript, renderRecaptchaWidget, resetRecaptchaWidget, getRecaptchaToken,
    loadRecaptchaV3Script, getRecaptchaV3Token,
    loadTurnstileScript, renderTurnstileWidget, resetTurnstileWidget, getTurnstileToken, removeTurnstileWidget
} from "@/lib/captcha";
import AgreementModal from "@/components/ui/AgreementModal";
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext";
import { useHeaderData } from "@/contexts/HeaderDataContext";
import { useCountries } from "@/contexts/CountriesContext";
import { resolveUrlFromMap, buildLocalizedLink } from "@/lib/blog-transformers";
import { useCaptchaConfig } from "@/hooks/useCaptchaConfig";
import { mapHonorificToGender, resolveReviewApiMessage } from "@/lib/review-form-utils";
import {
    PRODUCT_REVIEW_RATING_CATEGORIES,
    emptyProductReviewRatings,
    reviewCategoryLabel,
    type ProductReviewRatingCategoryKey,
} from "@/lib/product-review-rating-categories";
import type { PageUrlMap } from "@/types/blog";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { components } from "react-select";
import type { InputProps, GroupBase } from "react-select";

const ReactSelect = dynamic(() => import("react-select"), { ssr: false });

const NoAutocompleteInput = (props: InputProps<unknown, false, GroupBase<unknown>>) => {
    const uniqueId = useId();
    const { onFocus, readOnly: _readOnly, ...rest } = props;
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        e.target.removeAttribute("readonly");
        onFocus?.(e);
    };

    return (
        <components.Input
            {...rest}
            onFocus={handleFocus}
            autoComplete="nope"
            aria-autocomplete="list"
            data-lpignore="true"
            data-form-type="other"
            name={`review-country-${uniqueId.replace(/:/g, "")}`}
            readOnly
        />
    );
};

const labelClass = "font-semibold text-sm text-alsero-dark-gray mb-1.5 block";
const inputClass =
    "w-full border border-[rgba(0,0,0,0.08)] focus:border-alsero-first/40 focus:outline-none focus:ring-1 focus:ring-alsero-first/20 rounded-lg px-4 py-3 bg-white text-alsero-dark-gray text-sm transition-colors placeholder:text-gray-400";

const searchableSelectStyles = {
    control: (base: Record<string, unknown>, state: { isFocused?: boolean }) => ({
        ...base,
        borderColor: state.isFocused ? "rgba(227, 30, 36, 0.4)" : "rgba(0,0,0,0.08)",
        borderRadius: "0.5rem",
        minHeight: "48px",
        boxShadow: "none",
        fontSize: "0.875rem",
        "&:hover": { borderColor: "rgba(0,0,0,0.15)" },
    }),
    option: (base: Record<string, unknown>, state: { isSelected?: boolean; isFocused?: boolean }) => ({
        ...base,
        backgroundColor: state.isSelected ? "#E31E24" : state.isFocused ? "#fef2f2" : "white",
        color: state.isSelected ? "white" : "#4F545A",
        fontSize: "0.875rem",
        padding: "10px 16px",
    }),
    placeholder: (base: Record<string, unknown>) => ({ ...base, color: "#9CA3AF" }),
    singleValue: (base: Record<string, unknown>) => ({ ...base, color: "#4F545A" }),
    menu: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
    indicatorSeparator: () => ({ display: "none" as const }),
};

const motionItemProps = {
    initial: "hidden" as const,
    animate: "visible" as const,
};

interface AgreementItem {
    id: number;
    title: string;
    slug: string;
    content?: string;
    is_required: boolean;
}

interface FormAgreements {
    required: AgreementItem[];
    optional: AgreementItem[];
}

interface FormDetails {
    id: number;
    title: string;
    slug: string;
    language: {
        id?: number | null;
        name?: string | null;
        code?: string | null;
    };
    agreements: FormAgreements;
    fields?: Array<{
        type?: string;
        name?: string;
        field?: string;
        label?: string;
        title?: string;
        placeholder?: string;
        required?: boolean | string | number;
        rows?: number;
    }>;
    updated_at?: string | null;
}

interface ProductReviewFormProps {
    productId?: number | null;
    productType?: 'tour' | 'yacht';
    language?: string;
    languageId?: number | null;
    form?: {
        id?: number;
        slug?: string;
        title?: string;
    } | null;
    initialFormDetails?: FormDetails | null;
    translations?: Record<string, string>;
    pageUrls?: PageUrlMap;
    contractsPageUrl?: string;
    defaultLanguage?: string;
    onFormSubmit?: () => void; // Form submit sonrası callback
}

interface FormData {
    ratings: Record<ProductReviewRatingCategoryKey, number | null>;
    title: string;
    firstName: string;
    email: string;
    phone: string;
    country: string;
    review: string;
    agreements: Record<number, boolean>; // { agreementId: true/false }
}

interface FormErrors {
    ratings?: Record<string, string>;
    firstName?: string;
    email?: string;
    phone?: string;
    country?: string;
    review?: string;
    general?: string;
}

const RATING_CATEGORIES = PRODUCT_REVIEW_RATING_CATEGORIES;

const TITLE_OPTIONS = ["Mr", "Mrs", "Ms", "Miss", "Dr", "Other"].map((value) => ({
    value,
    label: value,
}));

export default function ProductReviewForm({
    productId,
    productType = 'tour',
    language = 'en',
    languageId,
    form = null,
    initialFormDetails = null,
    translations: serverTranslations,
    pageUrls,
    contractsPageUrl,
    defaultLanguage,
    onFormSubmit,
}: ProductReviewFormProps) {
    const { getVariable } = useLanguageVariables();
    const { languages: headerLanguages } = useHeaderData();
    const countries = useCountries();
    const isTurkish = language === 'tr' || language?.startsWith('tr');
    const translations = useMemo(() => serverTranslations ?? {}, [serverTranslations]);
    const t = useCallback((key: string, fallback = '') => {
        return translations[key] || getVariable(key, fallback);
    }, [translations, getVariable]);
    const captchaIdSeed = useId().replace(/:/g, "");
    const captchaContainerId = `product-review-captcha-container-${captchaIdSeed}`;

    const [formData, setFormData] = useState<FormData>({
        ratings: emptyProductReviewRatings(),
        title: '',
        firstName: '',
        email: '',
        phone: '',
        country: '',
        review: '',
        agreements: {},
    });
    
    const [formErrors, setFormErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [hoveredRatings, setHoveredRatings] = useState<Record<string, number | null>>({});
    
    // Form detayları state
    const [formDetails, setFormDetails] = useState<FormDetails | null>(initialFormDetails);
    const [formDetailsLoading, setFormDetailsLoading] = useState(false);
    const [formDetailsError, setFormDetailsError] = useState<string | null>(null);
    
    // Agreements state
    const [agreementsState, setAgreementsState] = useState<Record<number, boolean>>(() => {
        if (initialFormDetails?.agreements) {
            const state: Record<number, boolean> = {};
            (initialFormDetails.agreements.required || []).forEach((agreement) => {
                state[agreement.id] = true;
            });
            (initialFormDetails.agreements.optional || []).forEach((agreement) => {
                state[agreement.id] = false;
            });
            return state;
        }
        return {};
    });
    
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedAgreementSlug, setSelectedAgreementSlug] = useState<string | null>(null);
    const [selectedAgreementId, setSelectedAgreementId] = useState<number | undefined>(undefined);
    const [selectedAgreementIsRequired, setSelectedAgreementIsRequired] = useState(false);
    const formRef = useRef<HTMLFormElement | null>(null);
    const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const captchaWidgetIdRef = useRef<number | string | null>(null); // v2: number, Turnstile: string, v3: null
    const captchaContainerRef = useRef<HTMLDivElement | null>(null);
    const [captchaToken, setCaptchaToken] = useState<string>('');

    // Captcha config hook
    const { config: captchaConfig, isLoading: captchaConfigLoading, error: captchaConfigError } = useCaptchaConfig(true);
    const captchaProvider = captchaConfig?.is_enabled && captchaConfig.site_key ? captchaConfig.provider : null;
    const isCaptchaRequired = Boolean(captchaProvider);
    const countryPlaceholder = t("review_form.placeholder.country", isTurkish ? "Ülke seçiniz" : "Select country");
    const countryOptions = useMemo(() => [
        { value: "", label: countryPlaceholder },
        ...countries.map((country) => {
            const label = country.name_translations?.[language] ?? country.name;
            return { value: label, label };
        }),
    ], [countries, countryPlaceholder, language]);
    const selectedCountry = formData.country
        ? countryOptions.find((option) => option.value === formData.country) ?? { value: formData.country, label: formData.country }
        : null;
    
    // Debug: Captcha config durumunu logla
    useEffect(() => {
   
    }, [captchaConfigLoading, captchaConfigError, captchaConfig]);

    // Captcha script'ini yükle ve widget'ı render et (provider'a göre)
    useEffect(() => {
 

        // Config yükleniyorsa bekle
        if (captchaConfigLoading) {
            
            return;
        }

        // Error varsa çık
        if (captchaConfigError) {
            
            return;
        }

        // Captcha aktif değilse çık
        if (!captchaConfig?.is_enabled) {
            
            return;
        }

        if (!captchaConfig?.site_key || !captchaConfig?.provider) {
          
            return;
        }

        const provider = captchaConfig.provider;
        const siteKey = captchaConfig.site_key;
        const scriptUrl = captchaConfig.script_url;
        
        

        const initCaptcha = async () => {
            try {
                if (provider === 'google_recaptcha_v2') {
                    // reCAPTCHA v2
                    await loadRecaptchaScript(scriptUrl || undefined);
                    
                const checkContainer = setInterval(() => {
                        const container = document.getElementById(captchaContainerId);
                        if (container && siteKey) {
                        clearInterval(checkContainer);
                        renderRecaptchaWidget(
                                captchaContainerId,
                                siteKey,
                            (token: string) => {
                                    setCaptchaToken(token);
                            }
                        ).then((widgetId) => {
                                captchaWidgetIdRef.current = widgetId;
                                
                        }).catch((error) => {
                                console.error('[ProductReviewForm] reCAPTCHA v2 widget render edilemedi:', error);
                        });
                    }
                }, 100);

                setTimeout(() => {
                    clearInterval(checkContainer);
                }, 10000);
                } else if (provider === 'google_recaptcha_v3' || provider === 'google_recaptcha_enterprise') {
                    // reCAPTCHA v3 veya Enterprise - Widget yok; Enterprise için enterprise.js kullan
                    await loadRecaptchaV3Script(siteKey, provider === 'google_recaptcha_enterprise' ? scriptUrl : undefined);
                    // v3/Enterprise için token form submit sırasında alınacak
                } else if (provider === 'cloudflare_turnstile') {
                    // Cloudflare Turnstile
                    await loadTurnstileScript(scriptUrl || undefined);
                    
                    const checkContainer = setInterval(() => {
                        const container = document.getElementById(captchaContainerId);
                        if (container && siteKey) {
                            clearInterval(checkContainer);
                            renderTurnstileWidget(
                                captchaContainerId,
                                siteKey,
                                (token: string) => {
                                    setCaptchaToken(token);
                                }
                            ).then((widgetId) => {
                                captchaWidgetIdRef.current = widgetId;
                                
                            }).catch((error) => {
                                console.error('[ProductReviewForm] Turnstile widget render edilemedi:', error);
                            });
                        }
                    }, 100);

                setTimeout(() => {
                    clearInterval(checkContainer);
                }, 5000);
                }
            } catch (error) {
                console.error('[ProductReviewForm] Captcha yüklenemedi:', error);
            }
        };

        // Form render edildikten sonra başlat
        const timeout = setTimeout(() => {
            initCaptcha();
        }, 1000);

        return () => {
            clearTimeout(timeout);
            // Cleanup: Widget'ı reset et
            if (captchaWidgetIdRef.current !== null && captchaConfig?.provider) {
                if (captchaConfig.provider === 'google_recaptcha_v2') {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig.provider === 'cloudflare_turnstile') {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                    removeTurnstileWidget(captchaWidgetIdRef.current as string);
                }
            }
        };
    }, [captchaConfig, captchaConfigLoading, formDetails, captchaContainerId]); // captchaConfig değiştiğinde yeniden render et

    // Form detaylarını yükle (eğer initialFormDetails yoksa)
    useEffect(() => {
        if (initialFormDetails) {
            return;
        }
        
        if (!form?.id && !form?.slug) {
            setFormDetails(null);
            setFormDetailsLoading(false);
            setFormDetailsError(null);
            setAgreementsState({});
            return;
        }

        const controller = new AbortController();
        const params = new URLSearchParams();
        if (form?.id) {
            params.set('form_id', String(form.id));
        } else if (form?.slug) {
            params.set('form_slug', form.slug);
        }

        const fetchFormDetails = async () => {
            try {
                setFormDetailsError(null);
                const url = `/api/forms/details?${params.toString()}`;
                
                loadingTimeoutRef.current = setTimeout(() => {
                    if (!controller.signal.aborted) {
                        setFormDetailsLoading(true);
                    }
                }, 200);
                
                const response = await fetch(url, {
                    signal: controller.signal,
                });
                const payload = await response.json();
                
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }
                
                if (!controller.signal.aborted) {
                    setFormDetailsLoading(false);
                }
                
                if (!response.ok || !payload?.success) {
                    const errorMessage = payload?.message || payload?.error || 'Form bilgileri alınamadı';
                    throw new Error(errorMessage);
                }
                
                if (!payload?.data) {
                    throw new Error('Form detayları response\'unda data bulunamadı');
                }
                
                const details = payload.data as FormDetails;
                setFormDetails(details);
                
                const nextAgreementsState: Record<number, boolean> = {};
                (details?.agreements?.required || []).forEach((agreement) => {
                    nextAgreementsState[agreement.id] = true;
                });
                (details?.agreements?.optional || []).forEach((agreement) => {
                    nextAgreementsState[agreement.id] = false;
                });
                setAgreementsState(nextAgreementsState);
            } catch (error) {
                if (controller.signal.aborted) {
                    return;
                }
                console.error('Failed to fetch form details:', error);
                setFormDetails(null);
                setAgreementsState({});
                setFormDetailsError(error instanceof Error ? error.message : 'Form bilgileri alınamadı');
            } finally {
                if (loadingTimeoutRef.current) {
                    clearTimeout(loadingTimeoutRef.current);
                    loadingTimeoutRef.current = null;
                }
                if (!controller.signal.aborted) {
                    setFormDetailsLoading(false);
                }
            }
        };

        fetchFormDetails();

        return () => {
            controller.abort();
            if (loadingTimeoutRef.current) {
                clearTimeout(loadingTimeoutRef.current);
                loadingTimeoutRef.current = null;
            }
        };
    }, [form?.id, form?.slug, initialFormDetails]);

    // Sözleşme URL'lerini oluştur
    const getAgreementUrl = useCallback((agreementSlug: string): string => {
        if (!language || !defaultLanguage) {
            return '#';
        }
        const currentLang = language;
        const defaultLang = defaultLanguage;
        
        const baseUrl = resolveUrlFromMap(
            pageUrls,
            'contracts',
            currentLang,
            defaultLang,
            contractsPageUrl || '/contracts'
        );

        return buildLocalizedLink(baseUrl, agreementSlug, defaultLang);
    }, [pageUrls, contractsPageUrl, language, defaultLanguage]);

    // Form validasyonu
    const validateForm = useCallback((): boolean => {
        const errors: FormErrors = {};

        // Rating kategorileri kontrolü
        const ratingErrors: Record<string, string> = {};
        RATING_CATEGORIES.forEach((category) => {
            if (!formData.ratings[category.key] || formData.ratings[category.key]! < 1 || formData.ratings[category.key]! > 5) {
                const categoryLabel = reviewCategoryLabel(category, language, t);
                ratingErrors[category.key] = t('review_form.error.rating_required', '{{category}} rating is required').replace('{{category}}', categoryLabel);
            }
        });
        if (Object.keys(ratingErrors).length > 0) {
            errors.ratings = ratingErrors;
        }

        // First name kontrolü
        if (!formData.firstName.trim() || formData.firstName.trim().length < 2) {
            errors.firstName = t('review_form.error.full_name_required', t('review_form.error.first_name_required', isTurkish ? 'İsim soyisim zorunludur' : 'Full name is required'));
        }

        // Email format kontrolü (opsiyonel ama doluysa geçerli olmalı)
        if (formData.email && formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) {
                errors.email = t('review_form.error.email_invalid', isTurkish ? 'Lütfen geçerli bir e-posta adresi girin' : 'Please enter a valid email address');
            }
        }

        // Review text kontrolü
        if (!formData.review.trim() || formData.review.trim().length < 10) {
            errors.review = formData.review.trim().length === 0 
                ? t('review_form.error.description_required', t('review_form.error.review_required', isTurkish ? 'Açıklama zorunludur' : 'Description is required'))
                : t('review_form.error.description_min_length', t('review_form.error.review_min_length', isTurkish ? 'Açıklama en az 10 karakter olmalıdır' : 'Description must be at least 10 characters'));
        }

        // Required agreements kontrolü yok - ContactForm'da olduğu gibi sadece linkler var, checkbox yok
        // Opsiyonel sözleşmeler için kontrol yapılmıyor (opsiyonel oldukları için)

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData, t, isTurkish]);

    // Form submit
    const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        if (!productId || !form?.id && !form?.slug) {
            setFormErrors({ general: t('review_form.error.missing_info', isTurkish ? 'Ürün veya form bilgisi eksik' : 'Product ID or form information is missing') });
            return;
        }

        // languageId yoksa context'teki header languages'dan al (ek /api/header isteği yok)
        let finalLanguageId = languageId;
        if (!finalLanguageId && headerLanguages.length > 0) {
            const currentLang = headerLanguages.find((lang) => lang.attributes?.code === language);
            finalLanguageId = currentLang?.id ?? undefined;
        }

        if (!finalLanguageId) {
            setFormErrors({ general: t('review_form.error.language_not_found', isTurkish ? 'Dil bilgisi bulunamadı. Lütfen sayfayı yenileyin.' : 'Language information not found. Please refresh the page.') });
            return;
        }

        setIsSubmitting(true);
        setFormErrors({});
        setSubmitSuccess(false);

        try {
            // Captcha token kontrolü (provider'a göre)
            let captchaToken = '';
            if (captchaConfig?.is_enabled && captchaConfig?.site_key && captchaConfig?.provider) {
                const provider = captchaConfig.provider;
                if (provider === 'google_recaptcha_v2') {
                    captchaToken = getRecaptchaToken(captchaWidgetIdRef.current as number | undefined);
            if (!captchaToken) {
                setFormErrors({
                    general: t('common.captcha_required', isTurkish ? 'Lütfen robot doğrulamayı tamamlayın.' : 'Please complete the robot verification.'),
                });
                setIsSubmitting(false);
                return;
                    }
                } else if (provider === 'google_recaptcha_v3' || provider === 'google_recaptcha_enterprise') {
                    try {
                        captchaToken = await getRecaptchaV3Token(captchaConfig.site_key, 'review_form');
                    } catch (error) {
                        console.error('reCAPTCHA v3 token alınamadı:', error);
                        setFormErrors({
                            general: t('common.captcha_required', isTurkish ? 'Lütfen robot doğrulamayı tamamlayın.' : 'Please complete the robot verification.'),
                        });
                        setIsSubmitting(false);
                        return;
                    }
                } else if (provider === 'cloudflare_turnstile') {
                    captchaToken = getTurnstileToken(captchaWidgetIdRef.current as string | undefined);
                    if (!captchaToken) {
                        setFormErrors({
                            general: t('common.captcha_required', isTurkish ? 'Lütfen robot doğrulamayı tamamlayın.' : 'Please complete the robot verification.'),
                        });
                        setIsSubmitting(false);
                        return;
                    }
                }
            }

            // Rating kategorilerini topla
            const ratingCategories: Record<string, number> = {};
            RATING_CATEGORIES.forEach((category) => {
                if (formData.ratings[category.key]) {
                    ratingCategories[category.key] = formData.ratings[category.key]!;
                }
            });

            // Ortalama rating hesapla
            const ratingValues = Object.values(ratingCategories);
            const averageRatingRaw = ratingValues.length > 0
                ? ratingValues.reduce((sum, val) => sum + val, 0) / ratingValues.length
                : 0;
            const averageRating = averageRatingRaw > 0
                ? Math.min(5, Math.max(1, Math.round(averageRatingRaw)))
                : 0;

            // Review oluştur (CustomerReviews tablosuna)
            // Not: Form submit yapmıyoruz, direkt review oluşturuyoruz
            const honorific = formData.title.trim() || null;
            const reviewPayload: Record<string, unknown> = {
                language_id: finalLanguageId,
                name: formData.firstName.trim(),
                guest_name: formData.firstName.trim(),
                guest_email: formData.email.trim() || null,
                gender: mapHonorificToGender(honorific),
                phone: formData.phone.trim() || null,
                country: formData.country.trim() || null,
                title: honorific,
                comment: formData.review.trim(),
                rating: averageRating,
                reviewable_type: 'product',
                reviewable_id: productId,
                recaptcha_token: captchaToken || undefined,
                rating_categories: ratingCategories,
            };
            if (form?.id) {
                reviewPayload.form_id = form.id;
            }
            if (form?.slug) {
                reviewPayload.form_slug = form.slug;
            }

            const reviewResponse = await fetch('/api/customer-reviews', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(reviewPayload),
            });

            const reviewResult = await reviewResponse.json();

            if (!reviewResponse.ok || !reviewResult.success) {
                const errorMessage = resolveReviewApiMessage(reviewResult, t);
                throw new Error(errorMessage);
            }

            // Başarılı
            setSubmitSuccess(true);
            setFormData({
                ratings: emptyProductReviewRatings(),
                title: '',
                firstName: '',
                email: '',
                phone: '',
                country: '',
                review: '',
                agreements: {},
            });
            setHoveredRatings({});
            setCaptchaToken(''); // Token'ı temizle

            // Agreements state'i sıfırla
            if (formDetails?.agreements) {
                const resetState: Record<number, boolean> = {};
                (formDetails.agreements.required || []).forEach((agreement) => {
                    resetState[agreement.id] = true;
                });
                (formDetails.agreements.optional || []).forEach((agreement) => {
                    resetState[agreement.id] = false;
                });
                setAgreementsState(resetState);
            }

            // Captcha widget'ını reset et
            if (captchaWidgetIdRef.current !== null) {
                if (captchaConfig?.provider === 'google_recaptcha_v2') {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig?.provider === 'cloudflare_turnstile') {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                }
            }

            // Form'u temizle
            if (formRef.current) {
                formRef.current.reset();
            }

            // Callback çağır
            if (onFormSubmit) {
                onFormSubmit();
            }

            // Success mesajını 5 saniye sonra kaldır
            setTimeout(() => {
                setSubmitSuccess(false);
            }, 5000);
        } catch (error) {
            console.error('[ProductReviewForm] Form submission error:', error);
            const errorMessage =
                error instanceof Error
                    ? error.message
                    : t('review_form.error.review_creation_failed', 'Review could not be created');
            setFormErrors({
                general: errorMessage,
            });
            
            // Captcha widget'ını reset et (hata durumunda)
            if (captchaWidgetIdRef.current !== null && captchaConfig?.provider) {
                if (captchaConfig.provider === 'google_recaptcha_v2') {
                    resetRecaptchaWidget(captchaWidgetIdRef.current as number);
                } else if (captchaConfig?.provider === 'cloudflare_turnstile') {
                    resetTurnstileWidget(captchaWidgetIdRef.current as string);
                }
            }
        } finally {
            setIsSubmitting(false);
        }
    }, [formData, productId, languageId, language, headerLanguages, validateForm, form, formDetails, agreementsState, onFormSubmit, captchaConfig, t, isTurkish]);

    // Rating seçimi
    const handleRatingClick = useCallback((categoryKey: string, rating: number) => {
        setFormData((prev) => ({
            ...prev,
            ratings: {
                ...prev.ratings,
                [categoryKey]: rating,
            },
        }));
        
        // Hata varsa temizle
        if (formErrors.ratings?.[categoryKey]) {
            setFormErrors((prev) => {
                const newErrors = { ...prev };
                if (newErrors.ratings) {
                    delete newErrors.ratings[categoryKey];
                }
                return newErrors;
            });
        }
    }, [formErrors.ratings]);

    // Input değişiklikleri
    const handleInputChange = useCallback((field: keyof Omit<FormData, 'ratings' | 'agreements'>, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        
        // Hata varsa temizle
        if (formErrors[field as keyof FormErrors]) {
            setFormErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field as keyof FormErrors];
                return newErrors;
            });
        }
    }, [formErrors]);

    const requiredAgreements = formDetails?.agreements?.required ?? [];
    const optionalAgreements = formDetails?.agreements?.optional ?? [];

    // Form yoksa veya yükleniyorsa göster
    if (!form || formDetailsLoading) {
        return (
            <div className="bg-gray-50 rounded-lg p-6 text-center">
                <p className="text-gray-500">
                    {formDetailsLoading 
                        ? t('review_form.loading', isTurkish ? 'Form yükleniyor...' : 'Loading form...')
                        : t('review_form.not_found', isTurkish ? 'Form bulunamadı' : 'Form not found')}
                </p>
            </div>
        );
    }

    if (formDetailsError) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-600 text-sm">{formDetailsError}</p>
            </div>
        );
    }

    return (
        <motion.div
            className="min-w-0 max-w-full overflow-hidden bg-white rounded-lg border border-gray-200 p-4 sm:p-6 md:p-8"
            variants={formItemVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Başlık - "leave a comment" */}
            <motion.h3
                className="text-xl md:text-2xl font-bold text-alsero-dark-gray mb-6 flex items-center gap-3"
                variants={formItemVariants}
            >
                <span className="w-1 h-8 bg-alsero-first"></span>
                <span>{t('review_form.title', isTurkish ? 'Yorum bırakın' : 'Leave a comment')}</span>
            </motion.h3>

            {submitSuccess && (
                <motion.div
                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <p className="text-green-800 text-sm">
                        {t('review_form.success_message', isTurkish ? 'Teşekkürler! Yorumunuz alındı ve onay bekliyor.' : 'Thank you! Your review has been submitted and is pending approval.')}
                    </p>
                </motion.div>
            )}

            {formErrors.general && (
                <motion.div
                    className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                >
                    <p className="text-red-800 text-sm">{formErrors.general}</p>
                </motion.div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
                {/* Rating Kategorileri - Resimdeki gibi alt alta */}
                <div className="space-y-5">
                    {RATING_CATEGORIES.map((category) => {
                        const currentRating = formData.ratings[category.key] || 0;
                        const hoveredRating = hoveredRatings[category.key] || 0;
                        const displayRating = hoveredRating || currentRating;
                        const categoryLabel = reviewCategoryLabel(category, language, t);

                        return (
                            <div
                                key={category.key}
                                className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
                            >
                                <label className="block shrink-0 text-sm font-semibold text-alsero-dark-gray sm:min-w-[140px]">
                                    {categoryLabel}*
                                </label>
                                <div className="flex min-w-0 flex-col gap-1 sm:items-end">
                                    <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
                                        {[1, 2, 3, 4, 5].map((rating) => {
                                            const isSelected = currentRating >= rating;
                                            const isHovered = hoveredRating >= rating;
                                            const shouldHighlight = isSelected || isHovered;

                                            return (
                                                <button
                                                    key={rating}
                                                    type="button"
                                                    onClick={() => handleRatingClick(category.key, rating)}
                                                    onMouseEnter={() =>
                                                        setHoveredRatings((prev) => ({ ...prev, [category.key]: rating }))
                                                    }
                                                    onMouseLeave={() =>
                                                        setHoveredRatings((prev) => ({ ...prev, [category.key]: null }))
                                                    }
                                                    className={`h-5 w-5 shrink-0 transition-colors sm:h-6 sm:w-6 ${
                                                        shouldHighlight
                                                            ? 'text-yellow-400'
                                                            : 'text-gray-300 hover:text-yellow-300'
                                                    }`}
                                                    aria-label={`Rate ${categoryLabel} ${rating} stars`}
                                                >
                                                    <svg
                                                        className="h-full w-full fill-current"
                                                        viewBox="0 0 20 20"
                                                    >
                                                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                                    </svg>
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {formErrors.ratings?.[category.key] && (
                                        <p className="text-sm text-red-500 sm:text-right">{formErrors.ratings[category.key]}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Contact fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Title */}
                    <div>
                        <label htmlFor="title" className={labelClass}>
                            {t('review_form.field.title', isTurkish ? 'Başlık' : 'Title')}
                        </label>
                        <ReactSelect
                            inputId="title"
                            value={formData.title ? TITLE_OPTIONS.find((option) => option.value === formData.title) ?? null : null}
                            onChange={(opt) => handleInputChange('title', (opt as { value: string } | null)?.value ?? "")}
                            options={TITLE_OPTIONS}
                            placeholder={t('review_form.placeholder.title', isTurkish ? 'Başlık seçiniz' : 'Select title')}
                            isClearable
                            isSearchable
                            styles={searchableSelectStyles}
                            noOptionsMessage={() => t("request_form.no_options", isTurkish ? "Seçenek yok" : "No options")}
                            loadingMessage={() => t("request_form.loading", isTurkish ? "Yükleniyor..." : "Loading...")}
                            classNamePrefix="request-form-select"
                        />
                    </div>

                    {/* Full name */}
                    <div>
                        <label htmlFor="full_name" className={labelClass}>
                            {t('review_form.field.full_name', isTurkish ? 'İsim Soyisim' : 'Full name')}<span className="text-red-500 ml-1">*</span>
                        </label>
                        <input
                            id="full_name"
                            name="full_name"
                            type="text"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                            className={`${inputClass} ${formErrors.firstName ? 'border-red-300' : ''}`}
                            placeholder={t('review_form.placeholder.full_name', isTurkish ? 'İsim soyisim' : 'Full name')}
                            required
                        />
                        {formErrors.firstName && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.firstName}</p>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Email Address */}
                    <div>
                        <label htmlFor="email" className={labelClass}>
                            {t('review_form.field.email', isTurkish ? 'E-posta' : 'Email Address')}
                        </label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`${inputClass} ${formErrors.email ? 'border-red-300' : ''}`}
                            placeholder={t('review_form.placeholder.email', 'email@example.com')}
                        />
                        {formErrors.email && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
                        )}
                    </div>

                    {/* Phone */}
                    <div>
                        <label htmlFor="phone" className={labelClass}>
                            {t('review_form.field.phone', isTurkish ? 'Telefon' : 'Phone')}
                        </label>
                        <PhoneInput
                            name="phone"
                            defaultCountry={isTurkish ? "tr" : "us"}
                            value={formData.phone}
                            onChange={(phoneValue) => handleInputChange('phone', phoneValue)}
                            placeholder={t('review_form.placeholder.phone', isTurkish ? 'Telefon numarası' : 'Phone')}
                            className={`border border-[rgba(0,0,0,0.08)] focus-within:border-[rgba(0,0,0,0.40)] transition-all duration-300 rounded-lg !text-[#4F545A] font-medium text-sm px-2 py-2 ${formErrors.phone ? 'border-red-300' : ''}`}
                            inputClassName="!border-0 w-full"
                            countrySelectorStyleProps={{
                                buttonClassName: "!border-0 !px-2 !rounded-lg transition-all duration-300 hover:bg-gray-50",
                                dropdownStyleProps: {
                                    className: "!bg-white !border !border-[rgba(0,0,0,0.08)] !rounded-lg !shadow-lg !mt-1 !max-h-64 !overflow-y-auto",
                                    listItemClassName: "!px-3 !py-2 hover:!bg-alsero-soft-white !transition-colors !duration-200 !cursor-pointer !flex !items-center !gap-2",
                                    listItemFlagClassName: "!text-lg !mr-2",
                                    listItemCountryNameClassName: "!text-gray-700 !font-medium !text-sm",
                                    listItemDialCodeClassName: "!text-gray-500 !text-sm !ml-auto",
                                },
                            }}
                        />
                        {formErrors.phone && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.phone}</p>
                        )}
                    </div>
                </div>

                {/* Country */}
                <div>
                    <label htmlFor="country" className={labelClass}>
                        {t('review_form.field.country', isTurkish ? 'Ülke' : 'Country')}
                    </label>
                    <ReactSelect
                        inputId="country"
                        value={selectedCountry}
                        onChange={(opt) => handleInputChange('country', (opt as { value: string } | null)?.value ?? "")}
                        options={countryOptions}
                        placeholder={countryPlaceholder}
                        isClearable
                        isSearchable
                        styles={searchableSelectStyles}
                        noOptionsMessage={() => t("request_form.no_options", isTurkish ? "Seçenek yok" : "No options")}
                        loadingMessage={() => t("request_form.loading", isTurkish ? "Yükleniyor..." : "Loading...")}
                        classNamePrefix="request-form-select"
                        components={{ Input: NoAutocompleteInput }}
                    />
                    {formErrors.country && (
                        <p className="mt-1 text-sm text-red-500">{formErrors.country}</p>
                    )}
                </div>

                {/* Description */}
                <div>
                    <label htmlFor="review" className={labelClass}>
                        {t('review_form.field.description', isTurkish ? 'Açıklama' : 'Description')}<span className="text-red-500 ml-1">*</span>
                    </label>
                    <textarea
                        id="review"
                        name="review"
                        rows={5}
                        value={formData.review}
                        onChange={(e) => handleInputChange('review', e.target.value)}
                        className={`${inputClass} resize-y min-h-[120px] ${formErrors.review ? 'border-red-300' : ''}`}
                        placeholder={t('review_form.placeholder.description', isTurkish ? 'Açıklamanızı yazın' : 'Description')}
                        required
                    />
                    {formErrors.review && (
                        <p className="mt-1 text-sm text-red-500">{formErrors.review}</p>
                    )}
                </div>

                {/* Agreements and captcha */}
                <div className="space-y-4">
                    {(requiredAgreements.length > 0 || optionalAgreements.length > 0) && (
                        <div className="space-y-3 pt-4 border-t border-[rgba(0,0,0,0.08)]">
                            <h4 className={labelClass}>{t("reservation.agreements", isTurkish ? "Sözleşmeler" : "Agreements")}</h4>
                            {requiredAgreements.length > 0 && (
                                <div className="text-alsero-dark-gray text-sm">
                                    {requiredAgreements.map((agreement, index) => (
                                        <span key={agreement.id}>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedAgreementSlug(agreement.slug);
                                                    setSelectedAgreementId(agreement.id);
                                                    setSelectedAgreementIsRequired(agreement.is_required);
                                                    setModalOpen(true);
                                                }}
                                                className="text-alsero-first hover:underline font-semibold"
                                            >
                                                {agreement.title}
                                            </button>
                                            {index < requiredAgreements.length - 1 ? (
                                                <span className="text-alsero-dark-gray"> {t("request_form.agreement_separator", isTurkish ? "buradan, " : "from here, ")} </span>
                                            ) : (
                                                <span className="text-alsero-dark-gray"> {t("request_form.agreement_suffix", isTurkish ? "buradan " : "from here ")} </span>
                                            )}
                                        </span>
                                    ))}
                                    {t("request_form.agreement_end", isTurkish ? "ulaşabilirsiniz." : "you can access them here.")}
                                </div>
                            )}
                            {optionalAgreements.map((agreement) => (
                                <div key={agreement.id} className="flex items-start gap-2">
                                    <input
                                        type="checkbox"
                                        id={`review-agreement-opt-${agreement.id}`}
                                        checked={agreementsState[agreement.id] || false}
                                        onChange={(event) => {
                                            const checked = event.target.checked;
                                            setAgreementsState((prev) => ({
                                                ...prev,
                                                [agreement.id]: checked,
                                            }));
                                        }}
                                        className="mt-0.5 h-4 w-4 text-alsero-first focus:ring-alsero-first border-alsero-soft-white rounded cursor-pointer"
                                    />
                                    <label htmlFor={`review-agreement-opt-${agreement.id}`} className="flex-1 text-xs text-alsero-dark-gray cursor-pointer">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSelectedAgreementSlug(agreement.slug);
                                                setSelectedAgreementId(agreement.id);
                                                setSelectedAgreementIsRequired(agreement.is_required);
                                                setModalOpen(true);
                                            }}
                                            className="text-alsero-first hover:underline font-semibold"
                                        >
                                            {agreement.title}
                                        </button>
                                        {t("request_form.agreement_optional_confirm", isTurkish ? "'ni okudum, onaylıyorum." : " – I have read and accept.")}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Captcha Widget - Provider'a göre göster */}
                    {captchaConfigLoading ? (
                        <div className="flex items-center gap-2 text-sm text-alsero-dark-gray">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-alsero-first border-t-transparent" />
                            {t('review_form.captcha_loading', isTurkish ? 'Doğrulama yükleniyor...' : 'Verification loading...')}
                        </div>
                    ) : captchaConfigError ? (
                        <div className="text-sm text-yellow-600 mb-2">
                            {t('review_form.captcha_error', isTurkish ? 'Doğrulama yapılandırması yüklenemedi.' : 'Verification configuration could not be loaded.')}
                        </div>
                    ) : isCaptchaRequired ? (
                        <>
                            {captchaProvider === 'google_recaptcha_v2' && (
                    <div className="flex justify-start">
                        <div 
                                        ref={captchaContainerRef}
                                        id={captchaContainerId}
                            className="g-recaptcha"
                                        style={{ minHeight: '78px' }}
                        />
                    </div>
                            )}
                            {captchaProvider === 'cloudflare_turnstile' && (
                                <div className="flex justify-start">
                                    <div 
                                        ref={captchaContainerRef}
                                        id={captchaContainerId}
                                        style={{ minHeight: '65px' }}
                                    />
                                </div>
                            )}
                            {(captchaProvider === 'google_recaptcha_v3' || captchaProvider === 'google_recaptcha_enterprise') && (
                                <div className="text-sm text-gray-500 mb-2">
                                    {t('common.captcha_auto_note', isTurkish ? 'Robot doğrulaması gönder sırasında otomatik yapılır.' : 'Verification is done automatically when you submit.')}
                                </div>
                            )}
                        </>
                    ) : null}
                    {formErrors.general && isCaptchaRequired && !captchaToken && (
                        <p className="text-sm text-red-500">{formErrors.general}</p>
                    )}
                </div>

                {/* Submit Button */}
                <motion.button
                    type="submit"
                    disabled={isSubmitting || captchaConfigLoading}
                    className="w-full bg-alsero-first text-white py-3 rounded-lg font-semibold hover:bg-alsero-first/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed uppercase"
                    variants={buttonVariants}
                    initial="rest"
                    whileHover={isSubmitting ? "rest" : "hover"}
                    whileTap={isSubmitting ? "rest" : "tap"}
                >
                    {isSubmitting 
                        ? t('review_form.button.submitting', isTurkish ? 'Gönderiliyor...' : 'Submitting...')
                        : t('review_form.button.post_comment', isTurkish ? 'YORUM GÖNDER' : 'POST COMMENT')}
                </motion.button>
            </form>

            {/* Agreement Modal */}
            <AgreementModal
                isOpen={modalOpen}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedAgreementSlug(null);
                    setSelectedAgreementId(undefined);
                    setSelectedAgreementIsRequired(false);
                }}
                agreementSlug={selectedAgreementSlug}
                language={language}
                agreementId={selectedAgreementId}
                isRequired={selectedAgreementIsRequired}
                onAccept={(agreementId) => {
                    setAgreementsState((prev) => ({
                        ...prev,
                        [agreementId]: true,
                    }));
                }}
            />
        </motion.div>
    );
}
